import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { salesAPI, productsAPI, stockAPI } from '../services/api';
import { Plus, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

function SalesForm() {
  const [formData, setFormData] = useState({
    deliveryDate: '',
    notes: '',
    items: [{ variantId: '', quantity: '' }]
  });
  const [stockWarnings, setStockWarnings] = useState([]);
  const [showStockCheck, setShowStockCheck] = useState(false);

  const queryClient = useQueryClient();

  const { data: products } = useQuery('products', productsAPI.getAll, {
    select: data => data.data
  });

  const { data: stockData } = useQuery('stock', stockAPI.getCurrent, {
    select: data => data.data
  });

  const createOrderMutation = useMutation(salesAPI.createOrder, {
    onSuccess: () => {
      toast.success('Ordre oprettet succesfuldt');
      setFormData({
        deliveryDate: '',
        notes: '',
        items: [{ variantId: '', quantity: '' }]
      });
      setStockWarnings([]);
      setShowStockCheck(false);
      queryClient.invalidateQueries(['sales', 'stock']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Fejl ved oprettelse af ordre');
    }
  });

  // Check stock when items change
  useEffect(() => {
    checkStockAvailability();
  }, [formData.items, stockData]);

  const checkStockAvailability = () => {
    if (!stockData || !formData.items.length) {
      setStockWarnings([]);
      return;
    }

    const warnings = [];
    
    formData.items.forEach((item, index) => {
      if (!item.variantId || !item.quantity) return;
      
      const stockItem = stockData.find(stock => stock.variantId === item.variantId);
      const requestedQty = parseInt(item.quantity);
      
      if (!stockItem) {
        warnings.push({
          index,
          type: 'NO_STOCK',
          message: 'Ingen lager data fundet',
          variantId: item.variantId,
          requested: requestedQty
        });
      } else {
        const available = stockItem.available || 0;
        if (requestedQty > available) {
          warnings.push({
            index,
            type: 'INSUFFICIENT',
            message: `Kun ${available} tilgængelig, ${requestedQty} bestilt`,
            variantId: item.variantId,
            available,
            requested: requestedQty,
            shortage: requestedQty - available
          });
        }
      }
    });

    setStockWarnings(warnings);
  };

  const getVariantName = (variantId) => {
    for (const product of products || []) {
      const variant = product.variants?.find(v => v.id === variantId);
      if (variant) {
        return `${product.name} - ${variant.sku}`;
      }
    }
    return 'Ukendt variant';
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { variantId: '', quantity: '' }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.items.some(item => !item.variantId || !item.quantity)) {
      toast.error('Udfyld venligst alle påkrævede felter');
      return;
    }

    // If there are stock warnings, show them first
    if (stockWarnings.length > 0 && !showStockCheck) {
      setShowStockCheck(true);
      return;
    }

    const orderData = {
      ...formData,
      items: formData.items.map(item => ({
        variantId: item.variantId,
        quantity: parseInt(item.quantity)
      }))
    };

    createOrderMutation.mutate(orderData);
  };

  const handleForceSubmit = () => {
    const orderData = {
      ...formData,
      items: formData.items.map(item => ({
        variantId: item.variantId,
        quantity: parseInt(item.quantity)
      }))
    };

    createOrderMutation.mutate(orderData);
  };

  const totalStockIssues = stockWarnings.length;
  const hasStockIssues = totalStockIssues > 0;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Ny Salgsordre</h2>
        
        {/* Stock Warnings */}
        {hasStockIssues && showStockCheck && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">
                  Lager advarsel ({totalStockIssues} problem{totalStockIssues > 1 ? 'er' : ''})
                </h3>
                <div className="space-y-2">
                  {stockWarnings.map((warning, idx) => (
                    <div key={idx} className="text-sm text-yellow-700">
                      <strong>{getVariantName(warning.variantId)}</strong>: {warning.message}
                      {warning.type === 'INSUFFICIENT' && (
                        <span className="text-red-600 font-medium">
                          {' '}(Mangel: {warning.shortage} stk)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={handleForceSubmit}
                    disabled={createOrderMutation.isLoading}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {createOrderMutation.isLoading ? 'Opretter...' : 'Opret ordre alligevel'}
                  </button>
                  <button
                    onClick={() => setShowStockCheck(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Tilbage til redigering
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Delivery Date */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Afhentings dato
              </label>
              <input
                type="date"
                value={formData.deliveryDate}
                onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Order Items */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Ordre Linjer
                {hasStockIssues && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                    <AlertTriangle size={12} className="mr-1" />
                    {totalStockIssues} lager problem{totalStockIssues > 1 ? 'er' : ''}
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Plus size={16} />
                <span>Tilføj</span>
              </button>
            </div>

            {formData.items.map((item, index) => {
              const itemWarning = stockWarnings.find(w => w.index === index);
              const hasWarning = !!itemWarning;
              
              return (
                <div key={index} className={`p-4 mb-4 border rounded-lg ${
                  hasWarning ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
                }`}>
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <select
                        value={item.variantId}
                        onChange={(e) => updateItem(index, 'variantId', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          hasWarning ? 'border-yellow-400' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="">Vælg variant...</option>
                        {products?.map(product => 
                          product.variants?.map(variant => (
                            <option key={variant.id} value={variant.id}>
                              {product.name} - {variant.sku} ({variant.dimensions.length}x{variant.dimensions.width}x{variant.dimensions.height}cm)
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="w-24">
                      <input
                        type="number"
                        placeholder="Antal"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          hasWarning ? 'border-yellow-400' : 'border-gray-300'
                        }`}
                        min="1"
                        required
                      />
                    </div>

                    {/* Stock Status Indicator */}
                    <div className="w-8 flex items-center justify-center">
                      {item.variantId && item.quantity ? (
                        hasWarning ? (
                          <AlertTriangle size={20} className="text-yellow-600" title={itemWarning.message} />
                        ) : (
                          <CheckCircle size={20} className="text-green-600" title="Lager OK" />
                        )
                      ) : null}
                    </div>

                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  {/* Stock Warning Details */}
                  {hasWarning && (
                    <div className="mt-2 text-sm text-yellow-700">
                      <strong>Lager problem:</strong> {itemWarning.message}
                      {itemWarning.type === 'INSUFFICIENT' && (
                        <div className="mt-1">
                          <span className="text-gray-600">Tilgængelig: {itemWarning.available}</span>
                          <span className="mx-2">•</span>
                          <span className="text-red-600">Mangel: {itemWarning.shortage} stk</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bemærkninger
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          <button
            type="submit"
            disabled={createOrderMutation.isLoading}
            className={`w-full py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
              hasStockIssues && !showStockCheck
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {createOrderMutation.isLoading 
              ? 'Opretter...' 
              : hasStockIssues && !showStockCheck
                ? `Tjek lager (${totalStockIssues} problem${totalStockIssues > 1 ? 'er' : ''})`
                : 'Opret Ordre'
            }
          </button>
        </form>
      </div>
    </div>
  );
}

export default SalesForm;